import { Box, Button, Grid2, IconButton, Typography } from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { DeleteOutline } from '@mui/icons-material';
import { FormikTimePicker } from '@/common/presentation/components/forms/fields/FormikTimePicker';
import FormikTypeahead from '@/common/presentation/components/forms/fields/FormikTypeahead';
import { ERPJobFormValues } from '../../../ERPJobValidationSchema';
import tid from '@/common/infrastructure/config/tid';
import { TEXT_COLORS } from '@/common/presentation/constants/colors';
import { useERPDailyDispatchTimes } from '../hook/useERPDailyDispatchTimes';
import { JOB_FORM_MODES, type JobFormMode } from '@/raptorjob/domain/constants/jobFormLabels';

interface ERPDailyDispatchTimesProps {
  mode?: JobFormMode;
}

export const ERPDailyDispatchTimes = ({ mode = JOB_FORM_MODES.CREATE }: ERPDailyDispatchTimesProps) => {
  const {
    showUpTimeActive,
    leaveByTimeActive,
    onJobTimeActive,
    initialTimeZone,
    timezoneError,
    timezoneLoading,
    fetchTimeZonesWithInitial,
    handleShowUpTimeRemove,
    handleLeaveByTimeRemove,
    handleOnJobTimeRemove,
    handleShowUpTimeAdd,
    handleLeaveByTimeAdd,
    handleOnJobTimeAdd,
    handleTimezoneChange
  } = useERPDailyDispatchTimes({ mode });

  return (
    <>
      <Typography variant="h6" sx={{ fontSize: '1rem', mb: 2, fontWeight: 600 }}>
        Daily Dispatch Times
      </Typography>
      <Box>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            columnGap: { xs: '0px', sm: '12px' },
            rowGap: 2
          }}
        >
          {showUpTimeActive && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <FormikTimePicker<ERPJobFormValues>
                id="showUpTime"
                label="Show Up Time"
                sx={{ flex: 1 }}
                {...tid('jobs:create:show-up-time-input')}
              />
              <IconButton title="Remove Show Up Time" onClick={handleShowUpTimeRemove} sx={{ mb: '20px' }}>
                <DeleteOutline color="error" />
              </IconButton>
            </Box>
          )}
          {leaveByTimeActive && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <FormikTimePicker<ERPJobFormValues>
                id="leaveByTime"
                label="Leave By Time"
                sx={{ flex: 1 }}
                {...tid('jobs:create:leave-by-time-input')}
              />
              <IconButton title="Remove Leave By" onClick={handleLeaveByTimeRemove} sx={{ mb: '20px' }}>
                <DeleteOutline color="error" />
              </IconButton>
            </Box>
          )}

          {onJobTimeActive && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <FormikTimePicker<ERPJobFormValues>
                id="onJobTime"
                label="On Job Time"
                sx={{ flex: 1 }}
                {...tid('jobs:create:on-job-time-input')}
              />
              <IconButton title="Remove On Job Time" onClick={handleOnJobTimeRemove} sx={{ mb: '20px' }}>
                <DeleteOutline color="error" />
              </IconButton>
            </Box>
          )}
        </Box>

        {/* Action Buttons for Time Options */}
        <Box sx={{ display: 'flex', gap: 2, mt: 1, flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'flex-start' }}>
          {!showUpTimeActive && (
            <Button
              type="button"
              variant="text"
              onClick={handleShowUpTimeAdd}
              startIcon={<AddCircleOutlineIcon />}
              sx={{ whiteSpace: 'nowrap' }}
              {...tid('jobs:create:add-show-up-time-button')}
            >
              ADD SHOW UP TIME
            </Button>
          )}
          {!leaveByTimeActive && (
            <Button
              type="button"
              variant="text"
              onClick={handleLeaveByTimeAdd}
              startIcon={<AddCircleOutlineIcon />}
              sx={{ whiteSpace: 'nowrap' }}
              {...tid('jobs:create:add-leave-by-time-button')}
            >
              ADD LEAVE BY TIME
            </Button>
          )}
          {!onJobTimeActive && (
            <Button
              type="button"
              variant="text"
              onClick={handleOnJobTimeAdd}
              startIcon={<AddCircleOutlineIcon />}
              sx={{ whiteSpace: 'nowrap' }}
              {...tid('jobs:create:add-on-job-time-button')}
            >
              ADD ON JOB TIME
            </Button>
          )}
        </Box>

        {/* Job Time Zone Field */}
        <Box sx={{ mt: 1 }}>
          <Grid2 container spacing={1.5} sx={{ alignItems: 'flex-start', flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
            <Grid2 size={{ xs: 12, sm: 'auto' }} sx={{ minWidth: { sm: '312px' }, maxWidth: { sm: '312px' } }}>
              <FormikTypeahead<string, ERPJobFormValues>
                id="timeZone"
                testId="jobs:create:time-zone-input"
                fetchData={fetchTimeZonesWithInitial}
                minSearchLength={0}
                placeholder="Job Time Zone"
                noOptionsText="No timezones found"
                initialOption={initialTimeZone || undefined}
                loading={timezoneLoading}
                loadInitialData={true}
                clearOnBlur={false}
                disableClearable={false}
                onChange={handleTimezoneChange}
              />
            </Grid2>
            <Grid2
              size={{ xs: 12, sm: 'grow' }}
              sx={{
                flex: { sm: '1 0 0' },
                display: 'flex',
                alignItems: 'center',
                minHeight: { sm: '56px' }
              }}
            >
              <Typography variant="caption" sx={{ lineHeight: 1.66, color: TEXT_COLORS.TEXT_SECONDARY }}>
                Use the time zone where the job will take place, regardless of the client or dispatch location.
              </Typography>
            </Grid2>
          </Grid2>
          {timezoneError && (
            <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
              {timezoneError.message}
            </Typography>
          )}
        </Box>
      </Box>
    </>
  );
};
